import { ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import type { CustomerRegistrationResponseFragment } from '@/graphql/api'
import { useAuth } from '@/plugins/auth'
import { getRoutingRegion, useRegionPlugin } from '@/plugins/region'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'

export type Redirect = string | { name: string } | undefined | null

export interface UseRegistrationResponse {
    hasAccount: Ref<boolean>
    handleRegistrationResponse: (
        response: CustomerRegistrationResponseFragment | undefined,
        redirect: Redirect | null
    ) => void
    redirectPending: () => void
}

export function useRegistrationResponse(
    switchToLogin: () => void
): UseRegistrationResponse {
    const { setAuth } = useAuth()
    const { routerPush } = useRouterNavigation()
    const router = useRouter()
    const { regionCode } = useRegionPlugin()

    let pendingRedirect: Redirect = undefined
    const hasAccount = ref(false)

    const doRedirect = (redirect: Redirect): void => {
        if (!redirect) {
            return
        }

        if (typeof redirect === 'string') {
            const route = router.resolve(redirect)

            route.params['region'] = getRoutingRegion(regionCode.value)
            routerPush(route).catch(() => {})
        } else {
            routerPush(redirect).catch(() => {})
        }
    }

    const handleRegistrationResponse = (
        response: CustomerRegistrationResponseFragment | undefined,
        redirect: Redirect | null
    ): void => {
        if (response) {
            setAuth(response.login)

            if (!response.login) {
                pendingRedirect = redirect
                hasAccount.value = true

                switchToLogin()

                return
            } else {
                doRedirect(redirect)
            }
        } else {
            doRedirect(redirect)
        }
    }

    const redirectPending = (): void => {
        doRedirect(pendingRedirect)
    }

    return {
        hasAccount,
        handleRegistrationResponse,
        redirectPending,
    }
}
